import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, Route, Routes, matchPath, useLocation } from 'react-router-dom';
import { useAuth } from '../auth';
import { AccountMenu } from '../components/AccountMenu';
import { askConfirm } from '../components/ask';
import { BrandLockup, useBrand, useSlugBrand } from '../components/Brand';
import { useTheme } from '../theme';
import { leaveViewAs, viewAsId } from '../viewAs';
import { ForgotPassword } from '../pages/SetPassword';
import { forgetSaved, setOwner, useAutoFlush, useOutbox } from './offline';
import { Onboarding, onboarded } from './Onboarding';
import { BillsIcon, EyeIcon, OffersIcon, TimeOffIcon, TodayIcon, WeekIcon } from './PortalIcons';
import { PortalBills } from './PortalBills';
import { PortalLogin } from './PortalLogin';
import { lastBusiness } from '../lastBusiness';
import { useSite } from '../site';
import { PortalOffers } from './PortalOffers';
import { PortalSchedule } from './PortalSchedule';
import { PortalTimeOff } from './PortalTimeOff';
import { PortalVisit } from './PortalVisit';
import { ViewingContext } from './shared';
import { Status } from './Status';

/** A nav item for where you are: styled as current and announced as the current page. */
const current = (on: boolean) =>
  on ? { className: 'active', 'aria-current': 'page' as const } : {};

const PAGES: [RegExp, string][] = [
  [/^\/$/, 'Today'],
  [/^\/week/, 'Upcoming'],
  [/^\/visits\//, 'Visit'],
  [/^\/offers/, 'Available jobs'],
  [/^\/time-off/, 'Time off'],
  [/^\/bills/, 'Bills'],
];

/**
 * The tab title names the screen, and moving to another screen puts focus on its heading,
 * so a screen reader says where you are. Not on first load: the browser starts at the top.
 */
function usePageChange(business: string, signedIn: boolean) {
  const { pathname } = useLocation();
  const first = useRef(true);
  const page = signedIn
    ? (PAGES.find(([re]) => re.test(pathname))?.[1] ?? 'Contractor portal')
    : 'Sign in';
  useEffect(() => {
    const want = `${page} · ${business || 'Contractor portal'}`;
    const fix = () => {
      if (document.title !== want) document.title = want;
    };
    fix();
    // The shared brand loader also sets the title when it arrives; the screen's name wins.
    const el = document.querySelector('title');
    const watch = el ? new MutationObserver(fix) : null;
    watch?.observe(el!, { childList: true, characterData: true, subtree: true });
    return () => watch?.disconnect();
  }, [page, business]);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const h1 = document.querySelector<HTMLElement>('#main h1');
    if (h1) {
      h1.tabIndex = -1;
      h1.focus();
    }
  }, [pathname]);
}

/**
 * The contractor's app, under /portal. Phone-first: four places at the bottom (Today, Upcoming,
 * Available, Time off). On a wide screen the same places move to a rail on the left and the
 * schedule shows the chosen visit beside the list, for planning at home.
 */
export function PortalApp() {
  const { user, signOut } = useAuth();
  const viewing = user?.viewedBy ?? null;
  const brand = useBrand(user?.id);
  // Signed out: the sign-in is the business's own. On its own domain the domain decides;
  // elsewhere it comes from its link or the one this phone last signed in to, unless the
  // contractor has asked for a different business.
  const site = useSite();
  const domainSlug = site?.slug;
  const { pathname, state } = useLocation();
  // A visit belongs to the tab it was opened from: Upcoming if it came from there, else Today.
  const onVisit = pathname.startsWith('/visits/');
  const from = (state as { from?: string } | null)?.from;
  const [choosing, setChoosing] = useState(false);
  const slug =
    domainSlug ||
    (choosing
      ? undefined
      : matchPath('/login/:slug', pathname)?.params.slug || lastBusiness() || undefined);
  const slugBrand = useSlugBrand(user ? undefined : slug);
  // Light or dark: the phone's own setting unless the contractor picks one (kept per browser).
  const { theme, setTheme } = useTheme();
  usePageChange(brand?.name ?? slugBrand?.name ?? '', !!user);
  // Set before any screen loads, so saved data and the queue belong to this person.
  // Someone being viewed has no queue and no saved data here, so they get the empty owner.
  setOwner(user && !viewing ? user.id : 0);
  useAutoFlush(!!user && !viewing);
  const { waiting } = useOutbox();
  // Whether the first-run screens were finished in this session; the phone remembers across sessions.
  const [welcomed, setWelcomed] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator && import.meta.env.PROD) {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // Without it the app still works online.
      });
    }
  }, []);

  if (user === undefined) return <p className="center">Loading…</p>;

  if (user === null && viewAsId() !== null) {
    return (
      <main className="p-auth">
        <p role="alert">Sign in to the office app first, then use “View portal as” again.</p>
        <button
          type="button"
          onClick={() => {
            leaveViewAs();
            window.location.assign('/');
          }}
        >
          Stop viewing
        </button>
      </main>
    );
  }

  if (user === null) {
    // Wait for the site rather than flash another business's sign-in on a business's own domain.
    if (site === undefined) return null;
    return (
      <main className="p-auth">
        <Routes>
          <Route path="/forgot-password" element={<ForgotPassword portal />} />
          <Route
            path="/login/:slug"
            element={
              <PortalLogin
                key={slug ?? ''}
                slug={slug}
                locked={!!domainSlug}
                onChooseOther={() => setChoosing(true)}
              />
            }
          />
          <Route
            path="*"
            element={
              <PortalLogin
                key={slug ?? ''}
                slug={slug}
                locked={!!domainSlug}
                onChooseOther={() => setChoosing(true)}
              />
            }
          />
        </Routes>
      </main>
    );
  }

  if (!viewing && !welcomed && !onboarded(user.id)) {
    return (
      <div className="portal">
        <Onboarding name={user.displayName} userId={user.id} onDone={() => setWelcomed(true)} />
      </div>
    );
  }

  const exitViewing = () => {
    leaveViewAs();
    window.close();
    // A tab the browser will not close goes back to the office app instead.
    window.location.assign('/');
  };
  const routes = (
    <Routes>
      <Route path="/" element={<PortalSchedule mode="today" />} />
      <Route path="/week" element={<PortalSchedule mode="week" />} />
      <Route path="/visits/:id" element={<PortalVisit />} />
      <Route path="/offers" element={<PortalOffers />} />
      <Route path="/time-off" element={<PortalTimeOff />} />
      <Route path="/bills" element={<PortalBills />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  return (
    <ViewingContext.Provider
      value={viewing ? { name: user.displayName, by: viewing.displayName } : null}
    >
      <div className={`portal p-shell${viewing ? ' is-viewing' : ''}`}>
        <a className="skip" href="#main">
          Skip to content
        </a>
        <header className="p-head">
          <BrandLockup />
          <span className="p-head-links">
            <AccountMenu
              name={user.displayName}
              theme={theme}
              onTheme={setTheme}
              settings={false}
              side="bottom"
              onSignOut={
                viewing
                  ? undefined
                  : async () => {
                      // The queue stays on the phone for this person, but nothing sends until they are back.
                      if (
                        waiting > 0 &&
                        !(await askConfirm({
                          title:
                            waiting === 1
                              ? '1 change is still waiting to send'
                              : `${waiting} changes are still waiting to send`,
                          body: `${waiting === 1 ? 'It' : 'They'} will be sent the next time you sign in on this phone.`,
                          confirmLabel: 'Sign out anyway',
                        }))
                      )
                        return;
                      forgetSaved();
                      void signOut();
                    }
              }
            />
          </span>
        </header>
        {viewing && (
          <div className="p-viewing" role="status">
            <EyeIcon />
            <span>
              <strong>Viewing as {user.displayName}</strong>
              <span className="p-viewing-sub">
                Read-only, so buttons are switched off. Signed in as {viewing.displayName}.
              </span>
            </span>
            <button type="button" onClick={exitViewing}>
              Exit
            </button>
          </div>
        )}
        <Status />
        <main id="main" className="p-main">
          {routes}
        </main>
        <nav className="p-nav" aria-label="Main">
          <Link to="/" {...current(onVisit ? from !== '/week' : pathname === '/')}>
            <TodayIcon />
            <span>Today</span>
          </Link>
          <Link to="/week" {...current(onVisit ? from === '/week' : pathname.startsWith('/week'))}>
            <WeekIcon />
            <span>Upcoming</span>
          </Link>
          <Link to="/offers" {...current(pathname.startsWith('/offers'))}>
            <OffersIcon />
            <span>Available</span>
          </Link>
          <Link to="/time-off" {...current(pathname.startsWith('/time-off'))}>
            <TimeOffIcon />
            <span>Time off</span>
          </Link>
          <Link to="/bills" {...current(pathname.startsWith('/bills'))}>
            <BillsIcon />
            <span>Bills</span>
          </Link>
        </nav>
      </div>
    </ViewingContext.Provider>
  );
}
